import { computed, ref, watch, type ComputedRef, type Ref } from "vue";

import type {
  Controller,
  GraphDatabase,
  ObjectIdRaw,
} from "@ctrl/controller-rpc";

import type ContextMenu from "@/components/context-menu/ContextMenu.vue";
import { createObject } from "@/components/graph/component";
import { getObjectTitle } from "@/components/graph/title";
import {
  getRoutineParentId,
  getTableObjectData,
} from "@/components/graph/utils";
import type { BaseLogger } from "@/components/inject";
import type {
  PagesForestNodeRef,
  PagesForestSource,
} from "@/components/pages/pagesForest";
import { definitiveTableId } from "@/components/tables";
import { pagesStoragePrefix } from "@/composables/usePages";
import { findNodeInForest, type PagesForestNode } from "@/page";
import { RouteNames } from "@/routes/types";
import type { Router } from "@/types";
import { moveCustomObject } from "@/utils/hierarchy";
import { isWellFormedObjectId } from "@/utils/id";
import { navigateTo, navigateToObject } from "@/utils/navigate";

/** A page row of one workspace's pages database, as the live query has it. */
export type TeamPageRow = {
  rowId: string;
  title: string;
  /** The raw `_routine.parent` object-id string (not yet parsed). */
  parentId: string | null;
};

/**
 * The `PagesForestSource` of one team workspace's pages (see `TeamPages.vue`):
 * pages are rows of the workspace's provisioned Page type, so the forest is
 * *derived* — recomputed from the live rows query (which ticks on adds,
 * renames, re-parents and removals) instead of being incrementally patched
 * like the personal source. Page nodes carry their row id (the route's `id`
 * query); host nodes — a page parented under a task, project or other custom
 * object of the workspace — carry the full graph object id, exactly the
 * string the `_routine.parent` pointers hold.
 *
 * Moves and subpage creations go through `moveCustomObject`, the same
 * primitive as the objects' own Move action: `_routine.parent` rewrite plus
 * the Object blocks in the old/new parents' notes.
 */
export function createTeamPagesSource(options: {
  controller: Controller;
  baseLogger: BaseLogger;
  router: Router;
  contextMenu: Ref<InstanceType<typeof ContextMenu>>;
  orgId: string;
  database: GraphDatabase;
  /** The database's rows, from the live query (see `TeamPages.vue`). */
  rows: ComputedRef<TeamPageRow[]>;
  /** Parse cache of raw `_routine.parent` strings (shared, see owner). */
  parsedParents: Ref<Record<string, ObjectIdRaw | null>>;
  /** Ask the owner to parse a raw pointer into `parsedParents`. */
  requestParse: (raw: string) => void;
  /** Ownership of the source's internal watchers (e.g. `releaser.defer`). */
  own: (stop: () => void) => void;
  /** Record the row as the last opened page (drives re-opening the pane). */
  recordOpened: (rowId: string) => void;
  /** The object editor focuses the (empty) title of a just-created page. */
  markJustCreated: (rowId: string) => void;
}): PagesForestSource {
  const {
    controller,
    baseLogger,
    router,
    contextMenu,
    orgId,
    database,
    rows,
    parsedParents,
    requestParse,
    own,
    recordOpened,
    markJustCreated,
  } = options;
  const logger = baseLogger.forModule("TeamPagesSource");

  // The pane's browser-store keys, nested under the organization and
  // workspace like the personal pane's. No migration of the keys older
  // versions wrote (hierarchy keyed by workspace only, collapsed folders by
  // organization only): worst case the manual ordering and collapse state
  // reset once.
  const storagePrefix = pagesStoragePrefix(orgId, database.workspace);
  const orderKey = `${storagePrefix}/hierarchy`;
  const collapsedKey = `${storagePrefix}/collapsed`;

  // Host objects pulled into the forest (full object id -> title and raw
  // parent pointer; null = the object row is gone, its pages root
  // themselves). Filled by the fetch watcher below; a change recomputes the
  // forest.
  const hosts = ref<
    Map<string, { title: string; parentRaw: string | null } | null>
  >(new Map());

  const sameDatabase = (parsed: ObjectIdRaw): boolean =>
    (!parsed.workspace_id || parsed.workspace_id === database.workspace) &&
    definitiveTableId(parsed.table_id) === definitiveTableId(database.id);

  const forest = computed<PagesForestNode[]>(() => {
    const list = rows.value;
    const rowIds = new Set(list.map((row) => row.rowId));

    // Parent edges (child node id -> parent node id) and the host objects
    // referenced by them. An unparsed or unrepresentable pointer (another
    // workspace, the row itself) roots the page.
    const parentOf = new Map<string, string>();
    const hostIds = new Set<string>();
    for (const row of list) {
      const raw = row.parentId;
      if (!raw) continue;
      const parsed = parsedParents.value[raw];
      if (!parsed) continue;
      if (parsed.workspace_id && parsed.workspace_id !== database.workspace)
        continue;
      if (sameDatabase(parsed)) {
        if (
          parsed.row_id &&
          parsed.row_id !== row.rowId &&
          rowIds.has(parsed.row_id)
        )
          parentOf.set(row.rowId, parsed.row_id);
      } else {
        hostIds.add(raw);
        parentOf.set(row.rowId, raw);
      }
    }

    // Hosts not fetched yet (or gone): their pages stay at the root this
    // round. Fetched hosts nest under their own pointer when it names a
    // page of the set or another included host.
    for (const hostId of hostIds) {
      const host = hosts.value.get(hostId);
      if (!host) {
        for (const [child, parent] of [...parentOf])
          if (parent === hostId) parentOf.delete(child);
        continue;
      }
      const raw = host.parentRaw;
      if (!raw) continue;
      const parsed = parsedParents.value[raw];
      if (parsed && sameDatabase(parsed)) {
        if (parsed.row_id && rowIds.has(parsed.row_id))
          parentOf.set(hostId, parsed.row_id);
      } else if (hostIds.has(raw)) parentOf.set(hostId, raw);
    }

    // Break pointer cycles: walking up from a node must never come back to
    // it (same policy as `fetchPagesForest`).
    for (const id of [...parentOf.keys()]) {
      const seen = new Set<string>([id]);
      let current = parentOf.get(id);
      while (current !== undefined) {
        if (seen.has(current)) {
          parentOf.delete(id);
          break;
        }
        seen.add(current);
        current = parentOf.get(current);
      }
    }

    const nodes = new Map<string, PagesForestNode>();
    for (const row of list)
      nodes.set(row.rowId, {
        id: row.rowId,
        kind: "page",
        title: row.title,
        children: [],
      });
    for (const hostId of hostIds) {
      const host = hosts.value.get(hostId);
      if (host)
        nodes.set(hostId, {
          id: hostId,
          kind: "object",
          title: host.title,
          children: [],
        });
    }
    const roots: PagesForestNode[] = [];
    for (const [id, node] of nodes) {
      const parentNode = nodes.get(parentOf.get(id) ?? "");
      if (parentNode) parentNode.children.push(node);
      else roots.push(node);
    }
    return roots;
  });

  // Fetch the hosts the forest wants but doesn't know yet: their title (a
  // null title = the object is gone) and their own `_routine.parent` (whose
  // raw form is handed to the shared parse cache). Watches the parse cache
  // too: a pointer is only recognized as a host once its parse lands, which
  // doesn't tick `rows`.
  const beingFetched = new Set<string>();
  own(
    watch(
      [rows, () => parsedParents.value] as const,
      () => {
        for (const row of rows.value) {
          const raw = row.parentId;
          if (!raw) continue;
          const parsed = parsedParents.value[raw];
          // Not parsed yet (the owner's parser is on it), a page parent, or
          // already known.
          if (!parsed || sameDatabase(parsed)) continue;
          if (parsed.workspace_id && parsed.workspace_id !== database.workspace)
            continue;
          if (hosts.value.has(raw) || beingFetched.has(raw)) continue;
          beingFetched.add(raw);
          void (async () => {
            try {
              const title = await getObjectTitle(controller, raw);
              if (title === null) {
                hosts.value.set(raw, null);
                return;
              }
              const result = (await controller.graph.object.get(raw)).release();
              const data = getTableObjectData(result);
              const parentRaw = data ? getRoutineParentId(data) : null;
              if (parentRaw) requestParse(parentRaw);
              hosts.value.set(raw, { title, parentRaw });
            } catch (error) {
              logger.error("fetching a host object failed", { raw, error });
              hosts.value.set(raw, null);
            } finally {
              beingFetched.delete(raw);
            }
          })();
        }
      },
      { immediate: true, deep: true }
    )
  );

  // The pane re-derives its display on this tick (see `PagesForestSource`).
  const changed = ref(0);
  own(
    watch(forest, () => {
      changed.value += 1;
    })
  );

  const fullObjectIdOfRow = (rowId: string) =>
    controller.graph.object.id.make({
      workspace_id: database.workspace,
      table_id: database.id,
      row_id: rowId,
    });

  const nodeFullObjectId = (node: PagesForestNodeRef) =>
    node.kind === "object"
      ? Promise.resolve(node.id)
      : fullObjectIdOfRow(node.id);

  const movePage = async (id: string, parentId: string | null) => {
    // Refuse a destination inside the page's own subtree (the pointers
    // would cycle); the forest mirrors the persisted pointers, so a local
    // walk is authoritative enough.
    const node = findNodeInForest(forest.value, id);
    if (
      node &&
      parentId !== null &&
      (parentId === id || findNodeInForest([node], parentId))
    ) {
      logger.error("movePage: destination inside the page's subtree", {
        id,
        parentId,
      });
      // Re-land the pane on the stored pointers (the drag mutated nothing,
      // but the lists' optimistic order may disagree with the forest).
      changed.value += 1;
      return;
    }
    const parentFullId =
      parentId === null
        ? null
        : isWellFormedObjectId(parentId)
          ? parentId
          : await fullObjectIdOfRow(parentId);
    try {
      await moveCustomObject(
        controller,
        baseLogger,
        database.workspace,
        { tableId: database.id, rowId: id },
        parentFullId
      );
      // On success no read-back is needed: the live rows query ticks on
      // the re-parent and the forest recomputes from the stored pointers.
    } catch (error) {
      // On failure nothing ticks: re-land the pane on the stored pointers
      // ourselves, or it keeps the drag's optimistic placement until an
      // unrelated rows update (the `movePage` contract, see
      // `PagesForestSource`). The pane logs the rethrown error.
      changed.value += 1;
      throw error;
    }
  };

  const openPageRow = async (
    rowId: string,
    event?: MouseEvent | KeyboardEvent
  ) => {
    recordOpened(rowId);
    await navigateTo(
      RouteNames.OrgPages,
      router,
      { params: { orgId }, query: { id: rowId } },
      event
    );
  };

  const createPage = async (parentId: string | null) => {
    const rowId = await createObject(database, controller, "");
    if (!rowId) return null;
    // File the fresh page under its parent in the background: the filing is
    // a long serial chain (see `moveCustomObject`: ~10 round trips plus the
    // pointer and notes-block writes) and the user is about to look at the
    // fresh page, not the parent's notes — the rows tick re-lands the pane
    // on the stored pointer when it completes. A failure loses only the
    // filing (the page stays at the root) and is logged.
    if (parentId !== null) {
      movePage(rowId, parentId).catch((error: unknown) => {
        logger.error("filing the fresh page under its parent failed", {
          rowId,
          parentId,
          error,
        });
      });
    }
    markJustCreated(rowId);
    await openPageRow(rowId);
    return rowId;
  };

  const preview = async (
    node: PagesForestNodeRef | null,
    event?: MouseEvent | KeyboardEvent
  ) => {
    if (node === null) {
      await navigateTo(RouteNames.OrgPages, router, { params: { orgId } });
      return;
    }
    if (node.kind === "page") {
      await openPageRow(node.id, event);
      return;
    }
    await navigateTo(
      RouteNames.OrgPages,
      router,
      { params: { orgId }, query: { id: node.id } },
      event
    );
  };

  const openFullScreen = async (
    node: PagesForestNodeRef,
    event?: MouseEvent | KeyboardEvent
  ) => {
    const fullId = await nodeFullObjectId(node);
    await navigateToObject(
      router,
      controller,
      { kind: "object", id: fullId },
      event
    );
  };

  const showContextMenu = async (
    node: PagesForestNodeRef,
    event: MouseEvent
  ) => {
    const raw =
      node.kind === "object"
        ? await controller.graph.object.id.parse(node.id)
        : {
            workspace_id: database.workspace,
            table_id: database.id,
            row_id: node.id,
          };
    contextMenu.value.show(
      { type: "object", value: { type: "object", ...raw } },
      event
    );
  };

  return {
    forest,
    changed,
    orderKey,
    collapsedKey,
    createPage,
    movePage,
    preview,
    openFullScreen,
    showContextMenu,
  };
}
